import React from 'react';
import { Layout } from '../components/Layout';

const TransparencyPage = () => {
  return (
    <Layout>
      <div className="py-12 md:py-20">
        <div className="max-w-[900px] mx-auto px-6">
          <h1 className="text-4xl md:text-5xl font-semibold text-black mb-6" data-testid="transparency-title">
            Transparency
          </h1>
          <p className="text-xl md:text-2xl leading-relaxed text-[#333333] mb-16">
            How AI Atlas ranks and recommends tools
          </p>

          <div className="space-y-12">
            <section>
              <h2 className="text-3xl md:text-4xl font-semibold text-black mb-4">
                Our Commitment
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-[#333333]">
                AI Atlas does not rank tools based on sponsorship or paid placements. Our rankings are based entirely on structured evaluation criteria designed to match your specific needs.
              </p>
            </section>

            <section>
              <h2 className="text-3xl md:text-4xl font-semibold text-black mb-4">
                How We Rank Tools
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-[#333333] mb-6">
                When you search for an AI tool, we evaluate every option using a scoring system with four key factors:
              </p>
              <div className="space-y-6">
                <div className="bg-white border-2 border-gray-100 p-6 rounded-2xl">
                  <h3 className="text-2xl font-semibold text-black mb-3">
                    Capability Match (45%)
                  </h3>
                  <p className="text-base md:text-lg leading-relaxed text-[#333333]">
                    How well the tool's features match what you want to accomplish. We analyze the tool's description, capabilities, and best use cases against your goal.
                  </p>
                </div>

                <div className="bg-white border-2 border-gray-100 p-6 rounded-2xl">
                  <h3 className="text-2xl font-semibold text-black mb-3">
                    Constraint Match (25%)
                  </h3>
                  <p className="text-base md:text-lg leading-relaxed text-[#333333]">
                    Whether the tool meets your specific requirements, such as pricing preferences, platform compatibility, and skill level.
                  </p>
                </div>

                <div className="bg-white border-2 border-gray-100 p-6 rounded-2xl">
                  <h3 className="text-2xl font-semibold text-black mb-3">
                    Tag Alignment (20%)
                  </h3>
                  <p className="text-base md:text-lg leading-relaxed text-[#333333]">
                    How closely the tool's tags and categories align with your search terms and intended use.
                  </p>
                </div>

                <div className="bg-white border-2 border-gray-100 p-6 rounded-2xl">
                  <h3 className="text-2xl font-semibold text-black mb-3">
                    Recency (10%)
                  </h3>
                  <p className="text-base md:text-lg leading-relaxed text-[#333333]">
                    How recently the tool information was updated. We prioritize tools with current data to ensure accuracy.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-3xl md:text-4xl font-semibold text-black mb-4">
                Why This Tool Was Recommended
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-[#333333]">
                Every recommendation includes a clear explanation of why we suggested that particular tool. This explanation is based on the same factors used in our ranking system, written in simple language.
              </p>
            </section>

            <section>
              <h2 className="text-3xl md:text-4xl font-semibold text-black mb-4">
                Data Quality and Staleness
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-[#333333] mb-4">
                We monitor the age of our tool data. If information hasn't been updated in over 90 days, we:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed text-[#333333]">
                  • Display a clear notice that details may be out of date
                </li>
                <li className="text-base md:text-lg leading-relaxed text-[#333333]">
                  • Flag the tool for review in our admin dashboard
                </li>
                <li className="text-base md:text-lg leading-relaxed text-[#333333]">
                  • Encourage users to verify current details on the official website
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-3xl md:text-4xl font-semibold text-black mb-4">
                No Hidden Incentives
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-[#333333]">
                AI Atlas currently operates without sponsorships or affiliate relationships. If we introduce affiliate links in the future, they will be clearly labeled and will not influence our ranking algorithm.
              </p>
            </section>

            <section>
              <h2 className="text-3xl md:text-4xl font-semibold text-black mb-4">
                Our Goal
              </h2>
              <p className="text-base md:text-lg leading-relaxed text-[#333333]">
                Our goal is to provide clear, honest guidance that helps you choose the right AI tool for your needs. We believe in transparency, simplicity, and putting your needs first.
              </p>
            </section>

            <section>
              <h2 className="text-3xl md:text-4xl font-semibold text-black mb-4">
                Disclaimer
              </h2>
              <div className="bg-gray-50 border-2 border-gray-200 p-8 rounded-2xl">
                <p className="text-base md:text-lg leading-relaxed text-[#333333] mb-4">
                  AI Atlas provides general informational guidance about AI tools.
                </p>
                <p className="text-base md:text-lg leading-relaxed text-[#333333] mb-4">
                  We aim to present clear and accurate information, but:
                </p>
                <ul className="space-y-3 ml-6 mb-4">
                  <li className="text-base md:text-lg leading-relaxed text-[#333333]">
                    • We do not guarantee the accuracy, completeness, pricing, availability, or performance of third-party tools.
                  </li>
                  <li className="text-base md:text-lg leading-relaxed text-[#333333]">
                    • AI tools may change features, pricing, or policies at any time.
                  </li>
                  <li className="text-base md:text-lg leading-relaxed text-[#333333]">
                    • Users should verify details directly on official tool websites before making decisions.
                  </li>
                  <li className="text-base md:text-lg leading-relaxed text-[#333333]">
                    • AI Atlas does not endorse, sponsor, or guarantee any third-party tool unless explicitly stated.
                  </li>
                  <li className="text-base md:text-lg leading-relaxed text-[#333333]">
                    • Use of any tool listed on AI Atlas is at your own discretion.
                  </li>
                </ul>
              </div>
            </section>
          </div>

          <div className="mt-16 bg-gray-50 border-2 border-gray-200 p-8 rounded-2xl text-center">
            <h2 className="text-2xl md:text-3xl font-semibold text-black mb-4">
              Questions About Our Process?
            </h2>
            <p className="text-base md:text-lg leading-relaxed text-[#333333]">
              We're committed to operating with complete transparency. Our ranking methodology is open and designed to serve your best interests.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TransparencyPage;